/**
 * assets/myaiga-core.js
 * ---------------------------------------------------------------------------
 * myAiga✓ — Adapter registry, membership resolver, global API, event bus.
 *
 * ARCHITECTURE
 * ------------
 * 1. Adapters (window.myAigaAdapters) each know how to answer ONE question:
 *      "what is this customer's membership status, according to source X?"
 *    Built-in adapters: customerTags, customerMetafields, cartAttributes,
 *    domAttribute, appObject (window.myAigaExternalStatus), custom (fetch).
 *    Any app can register a new adapter without touching this file:
 *
 *      window.myAigaAdapters.myApp = {
 *        resolve: function () { return Promise.resolve({ status: "member" }); }
 *      };
 *
 * 2. The Resolver tries adapters in the configured priority order
 *    (myAigaConfig.membership.sourcePriority) and returns the first
 *    confident result. Statuses: unknown | guest | non_member | member |
 *    expired | pending.
 *
 * 3. window.myAiga is the ONLY surface other code (theme snippets, other
 *    apps, future providers) should touch. It never assumes a specific
 *    backend.
 *
 * 4. Standard events (see EVENTS below) are dispatched on `window` so any
 *    script — ours or a third party's — can react without polling.
 * ---------------------------------------------------------------------------
 */
(function (window, document) {
  "use strict";

  var cfg = window.myAigaConfig || {};
  if (!cfg.enabled) {
    // Still expose a inert API so calling code never has to null-check.
    window.myAiga = {
      isMember: function () { return false; },
      getStatus: function () { return "unknown"; },
      getCustomer: function () { return null; },
      getBenefits: function () { return []; },
      refresh: function () { return Promise.resolve("unknown"); },
      openMembership: function () {},
      openLogin: function () {},
      openSignup: function () {},
    };
    return;
  }

  var EVENTS = {
    READY: "myaiga:ready",
    MEMBERSHIP_UPDATED: "myaiga:membership-updated",
    PRICE_UPDATED: "myaiga:price-updated",
    CART_UPDATED: "myaiga:cart-updated",
    CUSTOMER_UPDATED: "myaiga:customer-updated",
    LOGIN: "myaiga:login",
    LOGOUT: "myaiga:logout",
  };

  function log() {
    if (cfg.debugMode && window.console) {
      var args = ["[myAiga]"].concat(Array.prototype.slice.call(arguments));
      console.log.apply(console, args);
    }
  }

  function emit(name, detail) {
    window.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function isValidStatus(s) {
    return ["unknown", "guest", "non_member", "member", "expired", "pending"].indexOf(s) !== -1;
  }

  // -------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------
  var state = {
    status: "unknown",
    source: null,
    customer: window.myAigaCustomer || null, // set by myaiga-bootstrap.liquid: { id, loggedIn, tags }
    lastResolvedAt: 0,
    benefits: [],
  };

  // -------------------------------------------------------------------
  // Built-in adapters
  // Each adapter exposes: resolve() -> Promise<{status, meta}> | {status, meta} | null
  // Returning null/unknown means "no opinion, try the next adapter".
  // -------------------------------------------------------------------
  window.myAigaAdapters = window.myAigaAdapters || {};

  function ensureAdapter(name, impl) {
    if (!window.myAigaAdapters[name]) window.myAigaAdapters[name] = impl;
  }

  // 0. Plan just selected in THIS cart session — instant member benefits
  //    (pricing, fee waiver, checkout discount) the moment someone picks
  //    Monthly/Yearly on the cart page, without waiting for the real
  //    Shopify customer tag (which only lands after the order is placed
  //    by your fulfillment automation/app). Deliberately a SEPARATE cart
  //    attribute from myaiga_status (which is a read-back/export of the
  //    resolved status for checkout-side systems) so the two mechanisms
  //    never fight each other. Only ever says "member" or defers (null) —
  //    never overrides an actual expired/pending tag with a false
  //    negative, since it simply doesn't have an opinion on those cases.
  ensureAdapter("planSelected", {
    resolve: function () {
      var attrs = (window.myAigaCart && window.myAigaCart.attributes) || {};
      if (attrs.myaiga_plan_selected === "true") return { status: "member" };
      return null; // no opinion — defer to customer_tag/aitrillion/etc
    },
  });

  // 1. Shopify customer tags (liquid-rendered into window.myAigaCustomer.tags)
  ensureAdapter("customerTags", {
    resolve: function () {
      var customer = state.customer;
      if (!customer || !customer.loggedIn) {
        return { status: cfg.membership.treatGuestAsNonMember ? "non_member" : "guest" };
      }
      var tags = customer.tags || [];
      // membership.tag supports a comma-separated list (e.g. one tag per
      // plan: "myaigamembersmonthly,myaigamembersyearly") — a customer with
      // ANY of these tags is a confirmed member. Same for expired/pending.
      function tagList(raw) {
        return String(raw || "")
          .split(",")
          .map(function (t) { return t.trim(); })
          .filter(Boolean);
      }
      function hasAnyTag(list) {
        return list.some(function (t) { return tags.indexOf(t) !== -1; });
      }

      if (hasAnyTag(tagList(cfg.membership.expiredTag))) return { status: "expired" };
      if (hasAnyTag(tagList(cfg.membership.pendingTag))) return { status: "pending" };
      if (hasAnyTag(tagList(cfg.membership.tag))) return { status: "member" };
      return { status: "non_member" };
    },
  });

  // 2. Shopify customer metafield (liquid-rendered into window.myAigaCustomer.metafield)
  ensureAdapter("customerMetafields", {
    resolve: function () {
      var customer = state.customer;
      if (!customer || !customer.loggedIn) {
        return { status: cfg.membership.treatGuestAsNonMember ? "non_member" : "guest" };
      }
      var mf = customer.metafield; // truthy/string/status value rendered server-side
      if (mf === true || mf === "true" || mf === "member" || mf === "active") return { status: "member" };
      if (mf === "expired") return { status: "expired" };
      if (mf === "pending") return { status: "pending" };
      return { status: "non_member" };
    },
  });

  // 3. Cart attributes (useful when membership was set during this session
  //    by a discount/app before the customer object reflects it yet)
  ensureAdapter("cartAttributes", {
    resolve: function () {
      var attrs = (window.myAigaCart && window.myAigaCart.attributes) || {};
      if (!attrs.myaiga_status) return null; // no opinion
      return { status: isValidStatus(attrs.myaiga_status) ? attrs.myaiga_status : "unknown" };
    },
  });

  // 4. Generic DOM attribute, e.g. <body data-myaiga-status="member">,
  //    so any app can flip status without touching JS at all.
  ensureAdapter("domAttribute", {
    resolve: function () {
      var val = document.documentElement.getAttribute("data-myaiga-status") ||
        document.body.getAttribute("data-myaiga-status");
      if (!val) return null;
      return { status: isValidStatus(val) ? val : "unknown" };
    },
  });

  // 5. App-provided JS object: window.myAigaExternalStatus = { status: "member", benefits: [...] }
  ensureAdapter("appObject", {
    resolve: function () {
      var ext = window.myAigaExternalStatus;
      if (!ext || !ext.status) return null;
      return { status: isValidStatus(ext.status) ? ext.status : "unknown", meta: ext };
    },
  });

  // 6. Custom API/callback adapter — opt-in, only fires if a customEndpoint
  //    or a window.myAigaCustomResolver callback is configured. Never
  //    assumes a specific vendor's route or payload shape beyond
  //    { status, benefits }. Fails silently (see error handling, req #32).
  ensureAdapter("custom", {
    resolve: function () {
      if (typeof window.myAigaCustomResolver === "function") {
        try {
          return Promise.resolve(window.myAigaCustomResolver()).catch(function () {
            return null;
          });
        } catch (e) {
          return null;
        }
      }
      if (cfg.membership.customEndpoint) {
        return fetch(cfg.membership.customEndpoint, { credentials: "same-origin" })
          .then(function (res) {
            if (!res.ok) throw new Error("status " + res.status);
            return res.json();
          })
          .then(function (data) {
            if (!data || !data.status) return null;
            return { status: isValidStatus(data.status) ? data.status : "unknown", meta: data };
          })
          .catch(function (err) {
            log("custom adapter failed (non-fatal):", err.message);
            return null;
          });
      }
      return null;
    },
  });

  // -------------------------------------------------------------------
  // Resolver — tries adapters in configured order, first confident wins
  // -------------------------------------------------------------------
  function resolveStatus() {
    var order = (cfg.membership.sourcePriority && cfg.membership.sourcePriority.length)
      ? cfg.membership.sourcePriority
      : [cfg.membership.source];

    var i = 0;
    function tryNext() {
      if (i >= order.length) {
        return Promise.resolve({ status: "unknown", source: null });
      }
      var name = order[i++];
      var adapter = window.myAigaAdapters[name];
      if (!adapter || typeof adapter.resolve !== "function") return tryNext();

      var result;
      try {
        result = adapter.resolve();
      } catch (e) {
        log("adapter '" + name + "' threw (non-fatal):", e.message);
        return tryNext();
      }

      return Promise.resolve(result).then(function (r) {
        if (!r || !r.status || r.status === "unknown") return tryNext();
        return { status: r.status, source: name, meta: r.meta || null };
      }).catch(function () {
        return tryNext();
      });
    }
    return tryNext();
  }

  function applyResult(result, opts) {
    opts = opts || {};
    var prevStatus = state.status;
    state.status = result.status || "unknown";
    state.source = result.source;
    state.benefits = (result.meta && result.meta.benefits) || state.benefits;
    state.lastResolvedAt = Date.now();

    document.documentElement.setAttribute("data-myaiga-status", state.status);
    document.body.classList.toggle("myaiga-is-member", state.status === "member");

    // Patch elements whose *destination*, not just visibility, depends on
    // status (e.g. the header nav item links to /account for members and
    // to the signup page for everyone else).
    var navLink = document.querySelector("[data-myaiga-nav-item]");
    if (navLink) {
      var href = state.status === "member"
        ? navLink.getAttribute("data-myaiga-member-href")
        : navLink.getAttribute("data-myaiga-nonmember-href");
      if (href) navLink.setAttribute("href", href);
    }

    log("resolved status:", state.status, "via", state.source);

    if (!opts.silent && prevStatus !== state.status) {
      emit(EVENTS.MEMBERSHIP_UPDATED, { status: state.status, previous: prevStatus, source: state.source });
    }
    return state.status;
  }

  function refresh(opts) {
    return resolveStatus().then(function (result) {
      return applyResult(result, opts);
    });
  }

  // -------------------------------------------------------------------
  // Listen for external apps announcing a membership change, per spec:
  //   window.dispatchEvent(new CustomEvent('myaiga:membership-updated',
  //     { detail: { member: true } }))
  // We treat this as an authoritative override from the app layer and
  // re-resolve afterwards so all adapters stay consistent, without
  // re-emitting infinitely (guarded via opts.silent + direct state set).
  // -------------------------------------------------------------------
  var applyingExternal = false;
  window.addEventListener(EVENTS.MEMBERSHIP_UPDATED, function (e) {
    if (applyingExternal) return; // ignore our own re-emits
    var detail = e.detail || {};
    if (typeof detail.member === "boolean" && !detail.__internal) {
      applyingExternal = true;
      window.myAigaExternalStatus = window.myAigaExternalStatus || {};
      window.myAigaExternalStatus.status = detail.member ? "member" : "non_member";
      refresh().then(function () {
        applyingExternal = false;
      });
    }
  });

  window.addEventListener(EVENTS.CART_UPDATED, function () {
    refresh();
  });

  // -------------------------------------------------------------------
  // Global API
  // -------------------------------------------------------------------
  window.myAiga = {
    EVENTS: EVENTS,

    isMember: function () {
      return state.status === "member";
    },
    getStatus: function () {
      return state.status;
    },
    getCustomer: function () {
      return state.customer;
    },
    getBenefits: function () {
      return state.benefits || [];
    },
    getSource: function () {
      return state.source;
    },
    refresh: refresh,

    // Pricing helpers delegate to myaiga-pricing.js if present, otherwise
    // fall back gracefully to Shopify's own product price (req #32).
    getMemberPrice: function (product) {
      return window.myAigaPricing ? window.myAigaPricing.getMemberPrice(product) : null;
    },
    getNonMemberPrice: function (product) {
      return window.myAigaPricing ? window.myAigaPricing.getNonMemberPrice(product) : null;
    },
    getSavings: function (product) {
      return window.myAigaPricing ? window.myAigaPricing.getSavings(product) : null;
    },

    addToCart: function (payload) {
      return window.myAigaCartApi ? window.myAigaCartApi.addToCart(payload) : Promise.reject(new Error("myaiga-cart.js not loaded"));
    },
    updateCart: function (payload) {
      return window.myAigaCartApi ? window.myAigaCartApi.updateCart(payload) : Promise.reject(new Error("myaiga-cart.js not loaded"));
    },

    openMembership: function () {
      window.location.href = cfg.urls.account;
    },
    openLogin: function () {
      window.location.href = cfg.urls.login;
    },
    openSignup: function () {
      window.location.href = cfg.urls.signup;
    },
  };

  // -------------------------------------------------------------------
  // Debug panel (req #33) — off by default, never exposes secrets, only
  // ever attached when myaiga_debug_mode is explicitly enabled in Theme
  // Settings.
  // -------------------------------------------------------------------
  function renderDebugPanel() {
    if (!cfg.debugMode) return;
    var el = document.getElementById("myaiga-debug-panel");
    if (!el) {
      el = document.createElement("div");
      el.id = "myaiga-debug-panel";
      el.className = "myaiga-debug";
      document.body.appendChild(el);
    }
    var cart = window.myAigaCart || {};
    el.innerHTML =
      "myAiga\u2713 debug<br>" +
      "status: " + state.status + "<br>" +
      "source: " + (state.source || "\u2014") + "<br>" +
      "customer id: " + (state.customer && state.customer.id ? state.customer.id : "guest") + "<br>" +
      "adapter priority: " + JSON.stringify(cfg.membership.sourcePriority) + "<br>" +
      "cart item count: " + (cart.itemCount != null ? cart.itemCount : "\u2014") + "<br>" +
      "cart attrs: " + JSON.stringify(cart.attributes || {}) + "<br>" +
      "resolved at: " + new Date(state.lastResolvedAt).toLocaleTimeString();
  }
  window.addEventListener(EVENTS.READY, renderDebugPanel);
  window.addEventListener(EVENTS.MEMBERSHIP_UPDATED, renderDebugPanel);
  window.addEventListener(EVENTS.CART_UPDATED, renderDebugPanel);

  // -------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------
  document.addEventListener("DOMContentLoaded", function () {
    refresh({ silent: true }).then(function (status) {
      emit(EVENTS.READY, { status: status });
    });
  });

  // In case DOMContentLoaded already fired (deferred script edge case)
  if (document.readyState === "interactive" || document.readyState === "complete") {
    refresh({ silent: true }).then(function (status) {
      emit(EVENTS.READY, { status: status });
    });
  }
})(window, document);
